// MonthlyReportComplianceReport.jsx - Track missing and late monthly reports
// Hierarchical view: Month > Job Coach > Consumer > Authorizations
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const MonthlyReportComplianceReport = () => {
  const { user } = useContext(AuthContext);
  const tenant_id = user?.tenant_id;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    customer_id: "",
    user_id: "",
    status_filter: "all",
    months_back: 3,
  });
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);

  // Track expanded states
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedCoaches, setExpandedCoaches] = useState({});
  const [expandedConsumers, setExpandedConsumers] = useState({});

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [customerRes, userRes] = await Promise.all([
          secureAxios.get("/api/customers", { params: { tenant_id } }),
          secureAxios.get("/api/users", { params: { tenant_id } }),
        ]);
        setCustomers(customerRes.data || []);
        const jobCoaches = (userRes.data || []).filter(
          (u) =>
            u.role?.toLowerCase() === "job coach" ||
            u.role?.toLowerCase() === "admin"
        );
        setUsers(jobCoaches);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/monthly-report-compliance",
        {
          params: {
            tenant_id,
            customer_id: filters.customer_id || undefined,
            user_id: filters.user_id || undefined,
            status_filter: filters.status_filter,
            months_back: filters.months_back,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);

        // Auto-expand first month
        if (response.data.data?.length > 0) {
          setExpandedMonths({ [response.data.data[0].month_key]: true });
        }

        await auditApi.logEntry({
          action: "GENERATE_MONTHLY_COMPLIANCE_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading monthly report compliance:", err);
      toast.error("Failed to load monthly report compliance");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const toggleMonth = (monthKey) => {
    setExpandedMonths((prev) => ({ ...prev, [monthKey]: !prev[monthKey] }));
  };

  const toggleCoach = (monthKey, coachId) => {
    const key = `${monthKey}-${coachId}`;
    setExpandedCoaches((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleConsumer = (monthKey, coachId, consumerId) => {
    const key = `${monthKey}-${coachId}-${consumerId}`;
    setExpandedConsumers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getComplianceBadge = (rate) => {
    const numRate = parseFloat(rate) || 0;
    if (numRate >= 90)
      return { color: "success", text: `${numRate.toFixed(1)}%` };
    if (numRate >= 70)
      return { color: "warning", text: `${numRate.toFixed(1)}%` };
    return { color: "danger", text: `${numRate.toFixed(1)}%` };
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "submitted":
        return {
          color: "success",
          icon: "bi-check-circle-fill",
          text: "Submitted",
        };
      case "late":
        return { color: "warning", icon: "bi-clock-fill", text: "Late" };
      case "missing":
        return { color: "danger", icon: "bi-x-circle-fill", text: "Missing" };
      default:
        return {
          color: "secondary",
          icon: "bi-question-circle",
          text: "Unknown",
        };
    }
  };

  const getStatsDisplay = (stats) => {
    if (!stats) return null;
    return (
      <span className="ms-2 small">
        <span className="badge bg-success me-1" title="Submitted">
          {stats.submitted || 0}
        </span>
        <span className="badge bg-warning text-dark me-1" title="Late">
          {stats.late || 0}
        </span>
        <span className="badge bg-danger" title="Missing">
          {stats.missing || 0}
        </span>
      </span>
    );
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-calendar-check me-2"></i>
            Monthly Report Compliance
          </h2>
          <p className="text-muted mb-0">
            Track missing, late, and submitted monthly reports
          </p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadReport}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={filters.customer_id}
                onChange={(e) =>
                  setFilters({ ...filters, customer_id: e.target.value })
                }
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Job Coach</label>
              <select
                className="form-select"
                value={filters.user_id}
                onChange={(e) =>
                  setFilters({ ...filters, user_id: e.target.value })
                }
              >
                <option value="">All</option>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status_filter}
                onChange={(e) =>
                  setFilters({ ...filters, status_filter: e.target.value })
                }
              >
                <option value="all">All Statuses</option>
                <option value="missing">Missing Only</option>
                <option value="late">Late Only</option>
                <option value="submitted">Submitted Only</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Months Back</label>
              <select
                className="form-select"
                value={filters.months_back}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    months_back: parseInt(e.target.value),
                  })
                }
              >
                <option value="1">1 Month</option>
                <option value="3">3 Months</option>
                <option value="6">6 Months</option>
                <option value="12">12 Months</option>
              </select>
            </div>
            <div className="col-md-3">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setFilters({
                    customer_id: "",
                    user_id: "",
                    status_filter: "all",
                    months_back: 3,
                  });
                  setExpandedMonths({});
                  setExpandedCoaches({});
                  setExpandedConsumers({});
                }}
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="card bg-primary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Expected Reports
                </h6>
                <h4 className="card-title mb-0">{summary.expected}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card bg-success text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Submitted
                </h6>
                <h4 className="card-title mb-0">{summary.submitted}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card bg-warning text-dark h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Late</h6>
                <h4 className="card-title mb-0">{summary.late}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card bg-danger text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Missing</h6>
                <h4 className="card-title mb-0">{summary.missing}</h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Rate */}
      {summary && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Overall Compliance Rate</strong>
              <span
                className={`badge ${
                  summary.compliance_rate >= 90
                    ? "bg-success"
                    : summary.compliance_rate >= 70
                    ? "bg-warning text-dark"
                    : "bg-danger"
                }`}
              >
                {summary.compliance_rate?.toFixed(1)}%
              </span>
            </div>
            <div className="progress" style={{ height: "25px" }}>
              <div
                className="progress-bar bg-success"
                style={{
                  width: `${
                    summary.expected > 0
                      ? (summary.submitted / summary.expected) * 100
                      : 0
                  }%`,
                }}
              >
                {summary.submitted > 0 && "Submitted"}
              </div>
              <div
                className="progress-bar bg-warning"
                style={{
                  width: `${
                    summary.expected > 0
                      ? (summary.late / summary.expected) * 100
                      : 0
                  }%`,
                }}
              >
                {summary.late > 0 && "Late"}
              </div>
              <div
                className="progress-bar bg-danger"
                style={{
                  width: `${
                    summary.expected > 0
                      ? (summary.missing / summary.expected) * 100
                      : 0
                  }%`,
                }}
              >
                {summary.missing > 0 && "Missing"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Hierarchical Accordion */}
      {!loading && reportData.length > 0 && (
        <div className="accordion" id="complianceAccordion">
          {reportData.map((month) => (
            <div className="card mb-3" key={month.month_key}>
              {/* Month Header */}
              <div
                className="card-header d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#f8f9fa" }}
                onClick={() => toggleMonth(month.month_key)}
              >
                <div className="d-flex align-items-center">
                  <i
                    className={`bi ${
                      expandedMonths[month.month_key]
                        ? "bi-chevron-down"
                        : "bi-chevron-right"
                    } me-2`}
                  ></i>
                  <h5 className="mb-0">
                    <i className="bi bi-calendar3 me-2 text-primary"></i>
                    {month.month_label}
                  </h5>
                  {month.stats && getStatsDisplay(month.stats)}
                </div>
                <span
                  className={`badge bg-${
                    getComplianceBadge(month.compliance_rate).color
                  }`}
                >
                  {getComplianceBadge(month.compliance_rate).text}
                </span>
              </div>

              {/* Month Content - Job Coaches */}
              {expandedMonths[month.month_key] && (
                <div className="card-body p-0">
                  {month.job_coaches.length === 0 ? (
                    <div className="p-3 text-muted text-center">
                      No data for this month
                    </div>
                  ) : (
                    month.job_coaches.map((coach) => (
                      <div key={coach.job_coach_id} className="border-bottom">
                        {/* Job Coach Header */}
                        <div
                          className="d-flex justify-content-between align-items-center px-3 py-2"
                          style={{ cursor: "pointer", backgroundColor: "#fff" }}
                          onClick={() =>
                            toggleCoach(month.month_key, coach.job_coach_id)
                          }
                        >
                          <div className="d-flex align-items-center">
                            <i
                              className={`bi ${
                                expandedCoaches[
                                  `${month.month_key}-${coach.job_coach_id}`
                                ]
                                  ? "bi-chevron-down"
                                  : "bi-chevron-right"
                              } me-2 text-muted`}
                            ></i>
                            <i className="bi bi-person-badge me-2 text-secondary"></i>
                            <strong>{coach.job_coach_name}</strong>
                            {coach.stats && getStatsDisplay(coach.stats)}
                          </div>
                          <span
                            className={`badge bg-${
                              getComplianceBadge(coach.compliance_rate).color
                            }`}
                          >
                            {getComplianceBadge(coach.compliance_rate).text}
                          </span>
                        </div>

                        {/* Consumers under this Job Coach */}
                        {expandedCoaches[
                          `${month.month_key}-${coach.job_coach_id}`
                        ] && (
                          <div className="ps-4">
                            {coach.consumers.map((consumer) => (
                              <div
                                key={consumer.consumer_id}
                                className="border-top"
                                style={{ backgroundColor: "#fafafa" }}
                              >
                                {/* Consumer Header */}
                                <div
                                  className="d-flex justify-content-between align-items-center px-3 py-2"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    toggleConsumer(
                                      month.month_key,
                                      coach.job_coach_id,
                                      consumer.consumer_id
                                    )
                                  }
                                >
                                  <div className="d-flex align-items-center">
                                    <i
                                      className={`bi ${
                                        expandedConsumers[
                                          `${month.month_key}-${coach.job_coach_id}-${consumer.consumer_id}`
                                        ]
                                          ? "bi-chevron-down"
                                          : "bi-chevron-right"
                                      } me-2 text-muted`}
                                    ></i>
                                    <i className="bi bi-person me-2 text-info"></i>
                                    <span>{consumer.consumer_name}</span>
                                    <small className="text-muted ms-2">
                                      ({consumer.customer_name})
                                    </small>
                                    {consumer.stats &&
                                      getStatsDisplay(consumer.stats)}
                                  </div>
                                  <span
                                    className={`badge bg-${
                                      getComplianceBadge(
                                        consumer.compliance_rate
                                      ).color
                                    }`}
                                  >
                                    {
                                      getComplianceBadge(
                                        consumer.compliance_rate
                                      ).text
                                    }
                                  </span>
                                </div>

                                {/* Authorization Details */}
                                {expandedConsumers[
                                  `${month.month_key}-${coach.job_coach_id}-${consumer.consumer_id}`
                                ] && (
                                  <div className="ps-4 pb-2">
                                    <table
                                      className="table table-sm table-bordered mb-0 ms-3 me-3"
                                      style={{ width: "calc(100% - 1.5rem)" }}
                                    >
                                      <thead className="table-light">
                                        <tr>
                                          <th>Authorization</th>
                                          <th>Service</th>
                                          <th className="text-center">Hours</th>
                                          <th className="text-center">
                                            Status
                                          </th>
                                          <th>Submitted</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {consumer.auths.map((auth) => {
                                          const badge = getStatusBadge(
                                            auth.status
                                          );
                                          return (
                                            <tr key={auth.authorization_id}>
                                              <td className="small">
                                                {auth.authorization_number}
                                              </td>
                                              <td className="small">
                                                {auth.service_name || "N/A"}
                                              </td>
                                              <td className="text-center small">
                                                {(auth.hours_used || 0).toFixed(
                                                  1
                                                )}{" "}
                                                /{" "}
                                                {(
                                                  auth.authorization_hours || 0
                                                ).toFixed(1)}
                                              </td>
                                              <td className="text-center">
                                                <span
                                                  className={`badge bg-${badge.color}`}
                                                >
                                                  <i
                                                    className={`bi ${badge.icon} me-1`}
                                                  ></i>
                                                  {badge.text}
                                                </span>
                                                {auth.days_late > 0 && (
                                                  <small className="d-block text-danger">
                                                    {auth.days_late} days late
                                                  </small>
                                                )}
                                              </td>
                                              <td className="small">
                                                {auth.submitted_date ? (
                                                  <>
                                                    {new Date(
                                                      auth.submitted_date
                                                    ).toLocaleDateString()}
                                                    {auth.submitted_by && (
                                                      <small className="text-muted d-block">
                                                        by {auth.submitted_by}
                                                      </small>
                                                    )}
                                                  </>
                                                ) : (
                                                  <span className="text-muted">
                                                    -
                                                  </span>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* No Data */}
      {!loading && reportData.length === 0 && (
        <div className="text-center py-5">
          <i
            className="bi bi-check-circle text-success"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3">No Data Found</h5>
          <p className="text-muted">
            No compliance data found for the selected filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthlyReportComplianceReport;
